/**
 * @lines98/client/screens/board-screen — playable 9×9 board screen.
 *
 * UX gold-standard: docs/prototype/lines98-concept.html (owner-approved).
 * This screen mirrors prototype's `view-play` + `buildBoard`/`cellClick`/`draw`/
 * `renderFrameNext`. Mode is fixed to `predict` for T-5 (the only T-5 surface).
 *
 * MOUNT MODEL
 * -----------
 * `mountBoardScreen(host)` returns an `unmount()` function. Internally it:
 *   1. Renders an empty board frame ONCE.
 *   2. Subscribes к the gameStore — каждое изменение state триггерит лёгкое
 *      diff-обновление (only cells that changed contents repaint).
 *   3. Returns the unsubscribe callback for the caller to clean up на
 *      переходе screen → result/replay.
 *
 * No virtual DOM. State→draw diff is keyed by `rendered[i]` (mirrors prototype
 * line 559). This keeps re-render O(N) per state change.
 *
 * RULES OBSERVED:
 *   - Only `@lines98/shared` engine logic — no local `applyMove`/`replayScore` (AC-4).
 *   - Numeric constants imported from `@lines98/shared/config/balance` (no hardcode).
 *   - DAILY mode is NOT what this is — T-5 is local-only play; режим зафиксирован
 *     `predict`, but seed comes from `Math.random()` (or ?seed= for dev/QA), NOT
 *     from `daily_seed` (T-2 territory).
 */

import { findPath, reachMask } from '@lines98/shared';
import { N, NEXT_BALLS_PER_TURN } from '@lines98/shared/config/balance';
import type { CellValue, FinishReason, NextBall } from '@lines98/shared/types/game';
import { dateOfDayFromUtc } from '@lines98/shared/util';

import { mountInfoPopover, type InfoPopoverHandle } from '../components/info-popover.js';
import { createStatPlate } from '../components/stat-plate.js';
import { createCountdownPlate } from '../components/countdown-plate.js';
import { attachActionRail, type ActionRailHandle } from '../lib/action-rail.js';
import {
  animateMoveSlide,
  applySpawnPop,
  spawnLineClearOverlay,
  spawnLineClearGrowOverlay,
  spawnLineClearHoldOverlay,
  type MoveSlideHandle,
} from '../lib/board-animations.js';
import { type AutofitHandle } from '../lib/autofit-text.js';
import { getClientPrefs } from '../lib/client-prefs.js';
import { attachTitleFit } from '../lib/title-fit.js';
import { mountBoardView, type BoardViewHandle } from '../lib/board-view.js';
import {
  attachBoardNudge,
  scheduleSoftBeat,
  type BoardNudgeHandle,
  type SoftBeatHandle,
} from '../lib/attract-beat.js';
import { attachLiveAppearance } from '../lib/board-appearance.js';
import { isDevDaily } from '../lib/dev-flags.js';
import { confirmFinish, confirmNewGame, isRunAtStake } from '../lib/leave-guard.js';
import { heartbeatOnSave } from '../lib/game-session-heartbeat.js';
import { clearRun, loadRun, saveRun } from '../lib/in-progress-cache.js';
import { applyDailyCacheDiscipline } from '../lib/daily-finish.js';
import { snapshotFinalBoard, snapshotInProgressBoard } from '../lib/daily-final-state.js';
import { savePendingOwnRow } from '../lib/pending-own-row.js';
import {
  formatHms,
  msUntilNextRollover,
  startCountdown,
  type CountdownHandle,
} from '../services/countdown.js';
import { saveInProgress } from '../services/daily-cache.js';
import {
  submitFinishedDailyGame,
  submitFinishedAllTimeGame,
  type SubmitResult,
} from '../services/game-submit.js';
import { openModePreview } from '../lib/mode-preview.js';
import { t } from '../lib/i18n/index.js';
import { saveLastGame } from '../services/replay-store.js';
import { showToast } from '../ui/toast.js';
import {
  getEmptyCellCount,
  getGrid,
  getLines,
  getNext,
  getScore,
  useGameStore,
  type GameStoreState,
  type SelectedCell,
} from '../store/game-store.js';

// =============================================================================
// Mount + lifecycle
// =============================================================================

export interface ScreenHandle {
  unmount(): void;
}

interface BoardElements {
  scoreEl: HTMLElement;
  nicheEl: HTMLElement;
  boardEl: HTMLElement;
  movesEl: HTMLElement;
  endBtn: HTMLButtonElement;
  newBtn: HTMLButtonElement;
  cells: HTMLDivElement[]; // 81 cell divs, row-major
  rendered: string[]; // diff key per cell (mirrors прототип `rendered[]`)
  /**
   * Live-move slide state (board-feedback-animations.md §2.5 extended to live play,
   * brief-38 §A.2). While `animating` is true a ball is sliding to its destination
   * via the SHARED `animateMoveSlide` overlay — клик-обработчик игнорирует новые
   * клики до завершения слайда, чтобы не накладывать ходы. `slide` — активный
   * handle (отменяется на unmount). Visual-only: движок-семантика/replayScore не
   * затронуты (см. handleCellClick).
   */
  animating: boolean;
  slide: MoveSlideHandle | null;
}

export function mountBoardScreen(host: HTMLElement): ScreenHandle {
  host.innerHTML = '';
  const root = document.createElement('div');
  // .postament-screen — общий скелет раскладки (styles/postament.css); .board-screen
  // оставляет ТОЛЬКО board-specific внешний вид (рамка/клетки/шары/шрифты).
  // .screen-root — ствол-класс edge-clearance (global.css, safe-area-aware): отступ к
  // краю экрана наследуется от ствола, экран про safe-area не знает (feat/safe-area-trunk).
  root.className = 'board-screen postament-screen screen-root';
  root.innerHTML = templateMarkup();
  host.appendChild(root);

  // Игровая доска — ЕДИНЫЙ модуль (lib/board-view), интерактивный режим (клетки с
  // role/aria/dataset; обработчики вешаем ниже). Плашка Очки+Ходы+ниша — из модуля.
  const board = mountBoardView(must(root.querySelector<HTMLElement>('#play-wrap')), {
    interactive: true,
  });
  // Оформление (палитра × материал) — ЕДИНЫЙ ствол lib/board-appearance, реактивно к
  // client-prefs (AC-2): смена оформления в сессии перекрашивает живую доску БЕЗ
  // сброса партии (меняются только CSS-переменные/атрибут на frame, не сетка).
  const appearance = attachLiveAppearance(board.frame);
  const elements = collectElements(root, board);
  attachCellHandlers(elements);

  // Initial render uses current store state (game already started OR start now).
  const state0 = useGameStore.getState();
  if (state0.engineState === null) {
    useGameStore.getState().startNewGame(state0.seed > 0 ? state0.seed : undefined);
  }

  wireActions(elements);

  // Attract-беат CTA «Начать» (триггерный, owner-final 2026-06-28; lib/attract-beat —
  // общий модуль, тот же на daily). Активен ТОЛЬКО на время pre-start превью:
  //   · мягкий намёк через ~600мс после входа (заметить кнопку «Начать»);
  //   · сильный зов кнопки на промах-тап по доске-превью (доска ещё не активна).
  // Снимается при старте партии (preStart→false) и на unmount. Слушатель тапа
  // пассивный → реальные клики по клеткам после старта работают как раньше; логику
  // старта по «Начать» не трогает (доска в pre-start от тапа НЕ стартует — только беат).
  let preStartSoftBeat: SoftBeatHandle | null = null;
  let preStartNudge: BoardNudgeHandle | null = null;
  const clearPreStartNudge = (): void => {
    preStartSoftBeat?.cancel();
    preStartSoftBeat = null;
    preStartNudge?.detach();
    preStartNudge = null;
  };
  const applyPreStartNudge = (preStart: boolean): void => {
    clearPreStartNudge();
    if (!preStart) return;
    preStartSoftBeat = scheduleSoftBeat(elements.endBtn); // намёк при входе
    preStartNudge = attachBoardNudge(elements.boardEl, elements.endBtn); // зов на промах-тап
  };

  // Subscribe to store; light diff render per change.
  const unsubscribe = useGameStore.subscribe((s, prev) => {
    if (s.phase !== 'board') return;
    renderBoardDiff(elements, s, prev);
    // On the preStart→playing transition the primary rail button relabels
    // («Начать» → «Завершить заход») → re-fit the rail (compact threshold).
    if (s.preStart !== prev.preStart) {
      actionRail.refit();
      applyPreStartNudge(s.preStart); // навесить/снять attract-беат по новому состоянию
    }
    // Pre-start PREVIEW is non-scored → never auto-detect end-game / cache moves.
    if (s.preStart) return;
    maybeDetectEndGame(s);
    // Persist in-progress cache on accepted moves (daily only).
    if (s.moves.length !== prev.moves.length) {
      void maybeSaveInProgress(s);
    }
  });

  // First paint.
  renderBoardDiff(elements, useGameStore.getState(), useGameStore.getState());
  // Если экран открылся уже в pre-start превью — навесить attract-беат сразу
  // (подписка ловит только ПЕРЕХОДЫ preStart, начальное состояние — здесь).
  applyPreStartNudge(useGameStore.getState().preStart);

  // Авто-подбор размера ТЕКСТОВЫХ элементов панели ИЗМЕРЕНИЕМ (не подбором
  // коэффициентов — см. lib/autofit-text.ts). Кнопки действий обрабатывает общий
  // kit attachActionRail (ниже); здесь — только заголовок + статистика.
  //   · заголовок — capToDesign: потолок = дизайн-размер из CSS (в rail щедрый
  //     cell·0.8 → измерение заполняет узкую панель; в вертикали — базовый cell·0.5);
  //   · статистика — capToDesign: своего дизайн-размера, лишь уменьшается при
  //     нехватке ширины (а не раздувается на широкой панели).
  // Заголовок экрана — ОБЩИЙ модуль lib/title-fit (единое правило размера = шрифту
  // кнопки на всех экранах, owner-directive 2026-05-30). Строка статистики убрана,
  // отдельного autofit статистики больше нет.
  const titleFit: AutofitHandle = attachTitleFit(root);

  // ОБЩИЙ action-rail kit (lib/action-rail.ts): autofit кнопок (mode:'line',
  // uniform, ACTION_RAIL_FILL — одна дельта на проект) + компактная форма панели
  // при узкой --rail-w (2 кнопки ⓘ/☰ + попапы, docs/specs/board-compact-rail.md).
  // Тот же primitive, что и на daily/прочих экранах. Доска НЕ уменьшается.
  const actionRail = attachBoardActionRail(root);

  // Портретная «?»-кнопка справа от заголовка (owner-directive 2026-05-29): над
  // доской ТОЛЬКО заголовок, описательное (подзаголовок + «Линии от N») — в «?».
  // Видна только в stacked (CSS), как на daily. Контент строится от контекста
  // (casual/daily) текущего store.
  const infoPopover = attachBoardInfoPopover(root);

  // On-board mode-selector УБРАН (owner 2026-06-03, P5): режим выбран ПЛАШКОЙ в лобби
  // (mode-card), на доске кнопок выбора режима НЕТ. Превью режима + «Начать» (рейл,
  // POST /game/start, серверный seed, AC-2.2) — сохранены. Мёртвый модуль линии-
  // селектора (lib/context-line) + его autofit/CSS сняты (чистка 2026-06-05).

  // Rollover auto-finish watch (T-12 trigger A, core-daily-board.md §5.4 + AC-12):
  // only для daily attempts. When the live countdown hits 21:00 UTC, the in-progress
  // attempt is auto-finished with the current score (finish_reason='rollover') and
  // submitted into the attempt's OWN day's leaderboard. Casual games have no
  // deadline → no watch.
  const rolloverWatch = startRolloverWatch();

  return {
    unmount(): void {
      titleFit.detach();
      actionRail.stopCountdown(); // остановить живой таймер доски дня (П4) — без утечки interval
      actionRail.detach();
      if (infoPopover) infoPopover.unmount();
      if (rolloverWatch) rolloverWatch.stop();
      clearPreStartNudge(); // снять attract-беат (таймер намёка + слушатель тапа)
      // Снять активный слайд хода (если экран уходит во время анимации): cancel
      // делает seat+cleanup немедленно (commit отложенного хода + уборка overlay).
      if (elements.slide) elements.slide.cancel();
      appearance.detach();
      unsubscribe();
      board.unmount();
      host.innerHTML = '';
    },
  };
}

/**
 * Start the daily rollover auto-finish watch (T-12 trigger A). Returns a
 * `CountdownHandle` to stop on unmount, OR `null` for casual games (no deadline).
 *
 * At rollover (msRemaining ≤ 0) — and only once — auto-finish the in-progress
 * daily attempt: `endGame('rollover')` + `finalizeGame('rollover')`. The submit
 * uses the attempt's own `date_of_day` (the store's daily context), so the game
 * publishes into THAT day's leaderboard (core-daily-board.md §3.5 п.3, AC-12).
 * The player keeps their score — the attempt counts.
 *
 * Guard conditions before firing: still daily, not already finished, at least one
 * move played (an idle-on-board attempt with zero moves has nothing to submit —
 * per core-daily-board.md §2.2 the attempt only «starts» on the first valid move).
 *
 * **Detection.** `msUntilNextRollover` never returns 0 (at the boundary it jumps
 * to a fresh 24h window), so we can't watch for «msRemaining ≤ 0». Instead we
 * compare `dateOfDayFromUtc(clock())` to the attempt's own `date_of_day`: when
 * they differ, the 21:00-UTC boundary has been crossed → rollover. This is the
 * same date math the server uses (one source of truth), so the client and server
 * agree on exactly when the day rolled.
 *
 * `options.clock` is the time source (defaults to `Date.now`); the rest forward to
 * `startCountdown` for deterministic tests. Exported so the decision can be driven.
 */
export function startRolloverWatch(options: {
  clock?: () => number;
  intervalMs?: number;
  setInterval?: (cb: () => void, ms: number) => unknown;
  clearInterval?: (handle: unknown) => void;
} = {}): CountdownHandle | null {
  const ctx0 = useGameStore.getState().context;
  if (ctx0.kind !== 'daily') return null;
  const attemptDate = ctx0.dateOfDay;
  const clock = options.clock ?? (() => Date.now());
  let fired = false;
  return startCountdown(() => {
    if (fired) return;
    // Rollover = the current day (server date math) no longer matches the
    // attempt's day. Bare local clock is acceptable here: a deliberately wrong
    // device clock only ever closes the player's OWN game early (no exploit —
    // the seed is fixed, the server re-verifies + late-submit window covers it).
    if (dateOfDayFromUtc(clock()) === attemptDate) return;
    const s = useGameStore.getState();
    if (s.context.kind !== 'daily') return;
    if (s.finishReason) return; // already ended (board_full / player_finish)
    if (s.moves.length === 0) return; // attempt never started — nothing to submit
    fired = true;
    s.endGame('rollover');
    showToast(t('game.board.toastRollover', { score: formatNumber(getScore(s)) }));
    void finalizeGame('rollover');
  }, options);
}

/**
 * Смонтировать портретную «?»-кнопку board (host = .board-titleline, справа от
 * заголовка). Поповер: подзаголовок (контекст) + «Линии собираются от N» — то
 * описательное, что убрано из строки над доской. KPI «Свободно/Ходы» остаются
 * под доской. В rail «?» скрыта (CSS) — там видна полная колонка.
 */
function attachBoardInfoPopover(root: HTMLElement): InfoPopoverHandle | null {
  const host = root.querySelector<HTMLElement>('.board-titleline');
  if (!host) return null;
  const s = useGameStore.getState();
  // Описательные тексты убраны (owner-directive 2026-05-30: «seed виден в URL»,
  // «engine», «Линии от N»). «?» остаётся ТОЛЬКО на доске дня — показывает контекст
  // дня; в casual описывать нечего → без «?».
  if (s.context.kind !== 'daily') return null;
  const body = t('game.board.dailySub', { date: s.context.dateOfDay });
  return mountInfoPopover({
    title: t('game.board.titleDaily'),
    bodyText: body,
    triggerHost: host,
    variant: '?',
  });
}

/**
 * «Начать» из pre-start превью (T-A, lazy-on-preview). Серверная сессия УЖЕ выдана
 * на входе в превью (`lib/mode-preview`), поэтому «Начать» = чистый клиентский
 * переход `preStart→false` (`beginPreviewedGame`) БЕЗ второго `POST /game/start` и
 * БЕЗ перекладки доски (AC-A.2). Превью буквально становится зачётной партией.
 */
function beginScoredGame(): void {
  useGameStore.getState().beginPreviewedGame();
}

// =============================================================================
// Action rail — board подключает ОБЩИЙ kit (lib/action-rail.ts) со своим контентом
// попапов (docs/specs/board-compact-rail.md). Переиспользуемый primitive для всех
// экранов с боковой панелью; поведение board не изменилось (те же элементы,
// generic-классы, те же builders, та же дельта ACTION_RAIL_FILL=0.7).
// =============================================================================

/** Расширенный handle action-rail доски: + остановка живого таймера доски дня (П4). */
interface BoardActionRailHandle extends ActionRailHandle {
  /** Остановить countdown таймера доски дня (если он был запущен; иначе no-op). */
  stopCountdown(): void;
}

/**
 * RU-подпись таймера до роллровера (П4, owner-bug 2026-06-22): ЕДИНАЯ с idle/done-
 * формулировкой хаба доски дня (daily-board-screen «Следующая доска через») — §8.8
 * визуальная когерентность, один текст про «новую доску» во всех местах.
 */
function dailyCountdownLabel(): string {
  return t('game.board.countdownLabel');
}

/**
 * Создать плашку живого обратного отсчёта до роллровера (П4). Визуально = кнопке
 * (`.btn` базис → попадает в uniform-autofit группу action-rail рядом с «Завершить
 * заход», тот же `--btn-h`/шрифт от токенов). Неинтерактивна (`role="img"`). Структура —
 * отдельные узлы подпись/значение (как daily idle), питается СУЩЕСТВУЮЩИМ
 * `services/countdown.startCountdown` (rollover-математика/тик 1/сек НЕ дублируются).
 */
function createDailyCountdownPlate(): { element: HTMLElement; stop(): void } {
  // ЕДИНЫЙ примитив таймера доски дня (`components/countdown-plate`) — ровно ТОТ ЖЕ, что
  // в done-state доски дня: value-only stat-plate (только HH:MM:SS, без слов; подпись в
  // aria). owner 2026-06-22: «импортируй этот таймер — он уже примитив». Авто-финиш по
  // роллроверу здесь делает отдельный `startRolloverWatch`, поэтому onRollover плашке не
  // нужен — только отображение. `.board-daily-countdown` = модификатор (tabular-nums).
  const plate = createCountdownPlate({ ariaLabel: dailyCountdownLabel() });
  plate.element.classList.add('board-daily-countdown');
  return plate;
}

function attachBoardActionRail(root: HTMLElement): BoardActionRailHandle {
  const rail = root.querySelector<HTMLElement>('.board-rail');
  const actionsContainer = root.querySelector<HTMLElement>('.board-actions');
  const compactHost = root.querySelector<HTMLElement>('.rail-compact');
  const endBtn = root.querySelector<HTMLButtonElement>('#btn-end');
  const newBtn = root.querySelector<HTMLButtonElement>('#btn-new');
  if (!rail || !actionsContainer || !compactHost || !endBtn || !newBtn) {
    return { refit(): void {}, detach(): void {}, stopCountdown(): void {} };
  }

  // ДОСКА ДНЯ (П4, owner-bug 2026-06-22): кнопка «К доске дня» бессмысленна на игровом
  // экране зачётной партии → заменяем её ЖИВЫМ обратным отсчётом до роллровера. Убираем
  // `#btn-new` ЦЕЛИКОМ из DOM (НЕ display:none, AC-4.1) и ставим таймер-плашку в тот же
  // слот → она попадает в uniform-autofit группу (вместо newBtn). CASUAL не трогаем —
  // там `#btn-new` = «Новая игра» (регресс-гард).
  const isDaily = useGameStore.getState().context.kind === 'daily';
  let countdownStop: (() => void) | null = null;
  let secondaryAction: HTMLElement = newBtn;
  if (isDaily) {
    const countdown = createDailyCountdownPlate();
    countdownStop = countdown.stop;
    newBtn.replaceWith(countdown.element); // newBtn ИЗ DOM целиком (count 0), таймер на его место
    secondaryAction = countdown.element;
  }

  const railHandle = attachActionRail(root, {
    rail,
    actionsContainer,
    compactHost,
    actionButtons: [endBtn, secondaryAction],
    infoTitle: t('game.board.railInfoTitle'),
    buildInfoBody,
    buildActionsBody,
  });
  return {
    ...railHandle,
    stopCountdown(): void {
      if (countdownStop) countdownStop();
    },
  };
}

/** Тело ⓘ-попапа: заголовок + подзаголовок + живая статистика (из store). */
function buildInfoBody(): HTMLElement {
  const s = useGameStore.getState();
  const isDaily = s.context.kind === 'daily';
  const dateOfDay = s.context.kind === 'daily' ? s.context.dateOfDay : '';

  const wrap = document.createElement('div');

  const title = document.createElement('div');
  title.className = 'rail-popover-text-title';
  title.textContent = isDaily ? t('game.board.titleDaily') : t('game.board.titleStandard');

  // Подзаголовок («Локальная игра» / «Одна попытка») и «Линии от N» УБРАНЫ
  // (owner-directive 2026-05-30). Для daily оставляем дату как единственную мету.
  if (isDaily && dateOfDay) {
    const sub = document.createElement('div');
    sub.className = 'rail-popover-text-sub';
    sub.textContent = t('game.board.dailySub', { date: dateOfDay });
    wrap.append(title, sub);
  } else {
    wrap.append(title);
  }
  return wrap;
}

/** Тело ☰-попапа: кнопки действий полными лейблами (те же store-действия). */
function buildActionsBody(close: () => void): HTMLElement {
  const s = useGameStore.getState();
  const isDaily = s.context.kind === 'daily';
  const preStart = s.preStart;

  const wrap = document.createElement('div');
  wrap.className = 'rail-popover-actions';

  // PRE-START PREVIEW (T-A): «Начать» begins the scored game (preStart→false, no
  // reshuffle — AC-A.2) + «перебросить старт» re-rolls a fresh server session
  // (AC-A.3). Both are the line-selector controls in the compact popover form.
  if (preStart) {
    const start = document.createElement('button');
    start.type = 'button';
    start.className = 'btn primary';
    start.textContent = t('game.board.btnStart');
    start.addEventListener('click', () => {
      beginScoredGame();
      close();
    });

    // «Перебросить старт» — fresh `POST /game/start` (new session, new layout).
    // Shared module `lib/mode-preview` (one source — same as the rail re-roll).
    const reroll = document.createElement('button');
    reroll.type = 'button';
    reroll.className = 'btn';
    reroll.textContent = t('game.board.btnReroll');
    reroll.addEventListener('click', () => {
      void (async () => {
        await rerollPreview();
        close();
      })();
    });

    wrap.append(start, reroll);
    return wrap;
  }

  const end = document.createElement('button');
  end.type = 'button';
  end.className = 'btn primary';
  end.textContent = t('game.board.btnFinish');
  end.disabled = !!s.finishReason;
  end.addEventListener('click', () => {
    void (async () => {
      if (useGameStore.getState().finishReason) return;
      if (!(await confirmFinish())) return;
      const st = useGameStore.getState();
      if (st.finishReason) return;
      st.endGame('player_finish');
      void finalizeGame('player_finish');
      close();
    })();
  });

  // ДОСКА ДНЯ (П4, owner-bug 2026-06-22): в ☰-попапе на зачётной доске дня кнопка «К доске
  // дня» УБРАНА (AC-4.1) → вместо неё плашка обратного отсчёта (must-visible на узком rail-
  // compact, AC-4.2). Попап эфемерен (rail-popover.buildBody() пересобирает тело при КАЖДОМ
  // открытии) → НЕ запускаем здесь interval (утечка/лайфсайкл): берём СНИМОК времени из той
  // же rollover-математики (`msUntilNextRollover`+`formatHms`, single-source) — свежее
  // значение на каждое открытие. Живой 1/сек-тик — на ОСНОВНОЙ плашке в .board-actions
  // (createDailyCountdownPlate). CASUAL — кнопка «Новая игра» жива (регресс).
  if (isDaily) {
    // Снимок времени (попап эфемерен → без interval/утечки). ТОТ ЖЕ value-only примитив
    // (createStatPlate без label) → только HH:MM:SS, без слов, как на основной плашке.
    const plate = createStatPlate({
      ariaLabel: dailyCountdownLabel(),
      initialValue: formatHms(msUntilNextRollover(Date.now())),
    });
    plate.element.classList.add('board-daily-countdown');
    wrap.append(end, plate.element);
    return wrap;
  }

  const fresh = document.createElement('button');
  fresh.type = 'button';
  fresh.className = 'btn';
  fresh.textContent = t('game.board.btnNewGame');
  fresh.addEventListener('click', () => {
    void (async () => {
      // Same single-source restart intent as the main rail (all_time/casual → confirm +
      // reset, T-C / RP-6). Daily-ветка убрана (П4 — там таймер выше).
      await requestRestart();
      close();
    })();
  });

  wrap.append(end, fresh);
  return wrap;
}

// =============================================================================
// Template
// =============================================================================

function templateMarkup(): string {
  // .board-rail обёртка: в portrait — `display: contents` (header / stats /
  // actions участвуют в grid напрямую как раньше); в landscape — flex-column
  // с max-height = board_h, центрируется vertically → chrome edges
  // = board edges, нет «chrome ближе к краю экрана чем доска».
  return `
    <div class="board-rail postament-rail">
      <div class="board-header postament-header">
        <!-- Портрет: ЕДИНСТВЕННАЯ строка над доской = заголовок + «?» справа
             (owner-directive 2026-05-29). Подзаголовок + engine-tag + «Линии от N»
             уходят в «?»-поповер → доска не дёргается при переходе игра↔daily
             (одинаковая высота шапки). В rail (ландшафт) «?» скрыта, показывается
             полная колонка (title/subtitle/tag). -->
        <div class="board-titleline postament-titleline">
          <div class="board-title postament-title screen-title">${t('game.board.titleStandard')}</div>
        </div>
        <!-- Подзаголовок / engine-tag / «Линии от N» УБРАНЫ (owner-directive
             2026-05-30): на доске только заголовок + доска + кнопки. -->
      </div>

      <div class="board-actions postament-actions">
        <button class="btn primary" id="btn-end" type="button">${t('game.board.btnFinish')}</button>
        <button class="btn" id="btn-new" type="button">${t('game.board.btnNewGame')}</button>
      </div>

      <!-- Компактная форма панели (docs/specs/board-compact-rail.md): показывается
           ТОЛЬКО когда панель слишком узкая для полной обвязки (измеряемое условие,
           generic CSS-класс .rail-is-compact из action-rail.css). Две кнопки-
           триггера монтируются сюда (mountRailPopover): ⓘ — весь текст, ☰ —
           действия. Доска не уменьшается. -->
      <div class="rail-compact" aria-hidden="true"></div>
    </div>

    <!-- Игровую доску монтирует ЕДИНЫЙ модуль board-view (mountBoardView,
         interactive) в .play-wrap при mount. Клики/выделение/анимации вешает board-
         screen на board.cells. Плашка (Очки + Ходы + 3 шара) — из модуля (Вариант А). -->
    <div class="play-wrap postament-board" id="play-wrap"></div>
  `;
}

/** Human-readable mode name for the board title (free-play / preview). */
function modeName(mode: GameStoreState['mode']): string {
  return mode === 'classic'
    ? t('game.board.modeClassic')
    : mode === 'blind'
      ? t('game.board.modeBlind')
      : t('game.board.modeStandard');
}

/**
 * Update header + button labels to reflect current context (daily vs free-play /
 * preview). Called from renderBoardDiff so transitions reflect store state. In
 * free-play / preview the title shows the SELECTED mode (§5.2 — switching the
 * line-selector live-updates it; daily stays predict-only, §3.2).
 */
function updateContextLabels(root: HTMLElement, s: GameStoreState): void {
  const titleEl = root.querySelector<HTMLElement>('.board-title');
  const subtitleEl = root.querySelector<HTMLElement>('.board-subtitle');
  const newBtn = root.querySelector<HTMLButtonElement>('#btn-new');
  // In pre-start preview the secondary button is «Перебросить старт» (RP-2) — set by
  // the caller (renderBoardDiff button block). Don't overwrite it here; only the title
  // reflects the previewed mode.
  if (s.context.kind === 'daily') {
    if (titleEl) titleEl.textContent = t('game.board.titleDaily');
    if (subtitleEl) subtitleEl.textContent = t('game.board.dailySub', { date: s.context.dateOfDay });
    // daily: #btn-new заменён живым таймером до роллровера (П4) — лейбл-апдейт кнопки не нужен.
  } else {
    // Free-play заголовок = «Игра — {mode}» (owner-directive 2026-06-25: нет «Локальной»/
    // «Свободной» игры — просто «Игра»; {mode} оставляем как полезный контекст какой режим).
    // Реактивно через `t('game.board.titleFree')` — экран ре-монтируется на смену языка.
    if (titleEl) titleEl.textContent = t('game.board.titleFree', { mode: modeName(s.mode) });
    // Подзаголовок про seed УБРАН ПОЛНОСТЬЮ (owner-directive 2026-06-25: seed игроку не
    // показываем). Элемента `.board-subtitle` в шапке доски нет (owner-directive 2026-05-30 —
    // только заголовок + доска + кнопки), поэтому ничего не пишем; querySelector — null-safe.
    if (subtitleEl) subtitleEl.textContent = '';
    if (newBtn && !s.preStart) newBtn.textContent = t('game.board.btnNewGame');
  }
}

// =============================================================================
// Element collection + cell construction
// =============================================================================

// Элементы доски берём из ЕДИНОГО модуля (board-view): score/moves/niche — узлы
// плашки модуля, cells/boardEl — его сетка. Игровой diff-рендер и анимации (ниже)
// работают по этим ссылкам без изменений. Ходы теперь на плашке (Вариант А) —
// movesEl указывает на её [data-bv-moves], а не на строку статов.
function collectElements(root: HTMLElement, board: BoardViewHandle): BoardElements {
  return {
    scoreEl: must(board.frame.querySelector<HTMLElement>('[data-bv-score]')),
    nicheEl: must(board.frame.querySelector<HTMLElement>('[data-bv-niche]')),
    boardEl: board.boardEl,
    movesEl: must(board.frame.querySelector<HTMLElement>('[data-bv-moves]')),
    endBtn: must(root.querySelector<HTMLButtonElement>('#btn-end')),
    newBtn: must(root.querySelector<HTMLButtonElement>('#btn-new')),
    cells: [...board.cells],
    rendered: new Array(N * N).fill(''),
    animating: false,
    slide: null,
  };
}

// Клики/выделение/анимации — на клетки модуля (board.cells). board-view (interactive)
// уже проставил role/aria/dataset; здесь вешаем обработчики (по индексу → r,c).
function attachCellHandlers(el: BoardElements): void {
  el.cells.forEach((d, i) => {
    const r = Math.floor(i / N);
    const c = i % N;
    d.addEventListener('click', () => handleCellClick(r, c, el));
    // board-feedback-animations.md §2.2 (owner-directive 2026-05-27): на mouseleave
    // мгновенно гасим .flash-invalid, чтобы коралловая вспышка не доигрывала после
    // ухода курсора с недопустимой клетки. setTimeout-safety в handleCellClick
    // остаётся как fallback (если курсор не покидал клетку).
    d.addEventListener('mouseleave', () => {
      d.classList.remove('flash-invalid');
    });
  });
}

// =============================================================================
// Click handler (UI policy lives here; engine policy lives in shared)
// =============================================================================

function handleCellClick(r: number, c: number, el: BoardElements): void {
  const s = useGameStore.getState();
  if (s.finishReason) return; // board locked
  // Пока едет шар предыдущего хода — игнорируем клики (не накладываем ходы).
  // Слайд визуальный, но коммит хода отложен до его конца (см. ниже), поэтому
  // gate обязателен, иначе второй клик закоммитит ход поверх анимируемого.
  if (el.animating) return;

  const grid = getGrid(s);
  if (!grid) return;

  // Click on a ball — (re)select. Click on empty — attempt move.
  if (grid[r]![c] !== 0) {
    s.selectCell(r, c);
    return;
  }

  // Empty cell click без выбранного шара — silent (no-op).
  if (!s.selectedCell) return;

  const selected = s.selectedCell;

  // Слайд хода (board-feedback-animations.md §2.5, brief-38 §A.2): если цель
  // ДОСТИЖИМА, шар «едет» по BFS-пути ПЕРЕД фиксацией хода — ровно как в реплее
  // (тот же модуль `animateMoveSlide`, тот же `findPath`, без дублирования). Путь
  // считаем по pre-move grid; источник скрываем (его «играет» overlay); по приезду
  // коммитим ход тем же движком (`attemptMove`) → store-рендер ставит шар в цель +
  // spawn-pop/celebration штатно. Visual-only: порядок ходов, спавн, replayScore,
  // replay-parity не меняются (анимация поверх того же детерминированного хода).
  //
  // Гейт настройкой (brief-39 §B, owner-decision 2026-06-02): live-слайд проигрывается
  // ТОЛЬКО когда игрок включил его в настройках (`liveMoveAnimation`). **Default OFF** —
  // ход применяется мгновенно (через `s.attemptMove` ниже, как было в проде до brief-38
  // §A.2). Реплей-анимацию этот флаг НЕ трогает: реплей всегда анимирован своим
  // модулем (тот же `animateMoveSlide`), флаг живёт только в live-ветке board-screen.
  if (getClientPrefs().liveMoveAnimation) {
    const path = findPath(grid, { r: selected.r, c: selected.c }, { r, c });
    const color = grid[selected.r]![selected.c] ?? 0;
    if (path && path.length >= 2 && color !== 0) {
      startLiveSlide(el, selected, { r, c }, color, path);
      return;
    }
  }

  // Недостижимо / нет пути → ход всё равно отрабатываем через движок (вернёт !ok,
  // nopath) для тактильного фидбэка (shake/flash) — поведение как раньше.
  const result = s.attemptMove(r, c);
  if (!result.ok) {
    // Visual feedback (см. .cell.flash-invalid + .ball.shake-invalid в board.css):
    //   - destination cell: коралловая вспышка, плавно затухает (только подсветка, без shake);
    //   - selected ball: shake-анимация (только transform, без изменения цвета).
    // Анимации auto-revert через @keyframes; класс снимаем через setTimeout для clean state
    // (иначе при повторном invalid-клике reflow-restart trick может не сработать).
    const destCell = el.cells[r * N + c];
    if (destCell) {
      destCell.classList.remove('flash-invalid');
      void destCell.offsetWidth;
      destCell.classList.add('flash-invalid');
      setTimeout(() => destCell.classList.remove('flash-invalid'), 350);
    }
    if (selected) {
      const srcBall = el.cells[selected.r * N + selected.c]?.querySelector<HTMLElement>('.ball');
      if (srcBall) {
        srcBall.classList.remove('shake-invalid');
        void srcBall.offsetWidth;
        srcBall.classList.add('shake-invalid');
        setTimeout(() => srcBall.classList.remove('shake-invalid'), 350);
      }
    }
    // Tactile feedback на mobile через Vibration API (best-effort, try/catch — некоторые
    // iframe / permissions throw'ают; no-op без поддержки).
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(40);
      } catch {
        /* no-op */
      }
    }
  }
}

/**
 * Проиграть слайд хода в ЖИВОЙ игре (board-feedback-animations.md §2.5 + brief-38
 * §A.2), затем зафиксировать ход тем же движком — модель реплея, но в реальной
 * партии. Анимация — ОБЩИЙ модуль `animateMoveSlide` (НЕ дублируем), путь — общий
 * `findPath`. Зеркалит `replay-screen.playOneStep`:
 *   1. ставим выделение/гасим источник (его «играет» едущий overlay-шар),
 *   2. едем по BFS-пути (`animateMoveSlide` + комета/трапеция — те же, что в реплее),
 *   3. по приезду коммитим ход (`s.attemptMove`) → store-подписка штатно рендерит
 *      шар в цели + spawn-pop next-балов + line-clear celebration (renderBoardDiff).
 *
 * VISUAL-ONLY ИНВАРИАНТ (load-bearing): ход уже ВАЛИДЕН (`findPath` нашёл путь той
 * же связностью, что `hasPath` использует движок), поэтому отложенный `attemptMove`
 * по приезду даёт РОВНО ТОТ ЖЕ результат, спавн и `replayScore`, как и немедленный
 * (порядок ходов в `moves` не меняется — он один). Анимация — поверх детерминизма,
 * replay-parity остаётся зелёным (anti-cheat: партия = seed + moves, не тронуты).
 *
 * Источник скрываем на время слайда (visibility:hidden), чтобы не было дубля
 * «статичный шар на месте + едущий overlay». `attemptMove` обнулит исходную клетку
 * (шар переехал) при рендере — статичный шар-источник исчезнет вместе с reset
 * выделения. Gate `el.animating` (handleCellClick) не пускает новые ходы до конца.
 */
function startLiveSlide(
  el: BoardElements,
  selected: SelectedCell,
  target: { r: number; c: number },
  color: number,
  path: ReadonlyArray<{ r: number; c: number }>,
): void {
  el.animating = true;

  // Подсветка выделения уже стоит (selectCell). Гасим статичный шар-источник на
  // время слайда — едущий overlay его «играет».
  const srcBall = el.cells[selected.r * N + selected.c]?.querySelector<HTMLElement>('.ball');
  if (srcBall) srcBall.style.visibility = 'hidden';

  let committed = false;
  const commit = (): void => {
    if (committed) return;
    committed = true;
    el.slide = null;
    el.animating = false;
    const s = useGameStore.getState();
    // Партия могла завершиться/смениться во время слайда (rollover / unmount-cancel
    // не доходит сюда т.к. cancel зовёт seat→commit при ещё валидном state). Если
    // выделение слетело — мягко выходим: store сам отрисовал актуальное состояние.
    if (s.finishReason) return;
    if (!s.selectedCell || s.selectedCell.r !== selected.r || s.selectedCell.c !== selected.c) {
      return;
    }
    // Коммит хода тем же движком — store-подписка рендерит post-move state
    // (spawn-pop / celebration штатно). Тот же путь, что был без слайда.
    s.attemptMove(target.r, target.c);
  };

  // Live-игра всегда speed=1 (регулятор скорости — только в реплее).
  el.slide = animateMoveSlide(el.boardEl, el.cells, color, path, 1, commit);
}

// =============================================================================
// Diff renderer (state → DOM, only touching cells that changed)
// =============================================================================

function renderBoardDiff(
  el: BoardElements,
  s: GameStoreState,
  prev: GameStoreState,
): void {
  const grid = getGrid(s);
  if (!grid) return;

  const next = getNext(s);
  // Mode controls ONLY the visibility of `next` (extra-modes §2.1; matrix
  // replays-ghosts.md §2.3). The engine always GENERATES `next` (determinism is
  // mode-independent — AC-2); we only choose what the player sees:
  //   - predict → ghosts on cells + niche.
  //   - classic → niche only (no ghosts).
  //   - blind   → neither.
  // Ghosts are suppressed by feeding an EMPTY ghost index to the cell renderer;
  // the niche is hidden by `renderNiche(..., show=false)`.
  const showGhosts = s.mode === 'predict';
  const showNiche = s.mode === 'predict' || s.mode === 'classic';
  const ghostIndex = showGhosts ? buildGhostIndex(next) : EMPTY_GHOST_INDEX;

  // Reachable mask: BFS от выбранного шара через пустые клетки. Пустые клетки
  // ВНЕ маски получают .unreachable → hover-feedback (коралл + cursor not-allowed).
  // Без выделения mask = null, все клетки нейтральны.
  const mask =
    s.selectedCell !== null
      ? reachMask(grid, s.selectedCell.r, s.selectedCell.c)
      : null;

  // spawn-pop policy (board-feedback-animations.md §2.4, owner-directive
  // 2026-05-31): the appearance animation marks balls that REALLY APPEAR DURING
  // PLAY — i.e. next-balls materialized by `spawnNext` after a move (+ grown line
  // cells, handled by the line-clear overlays below). The STARTING layout (first
  // paint, fresh casual/daily start) is rendered STATICALLY, consistent with the
  // daily preview (`board-view.renderStaticGrid`): otherwise mounting the live
  // board on «Играть доску дня» pops every starting ball → looks like the
  // preview's static balls «refreshing».
  //
  // Recovery guard (§2.4 / AC-13): daily-recovery replay (resumeInProgress) DOES
  // grow `s.moves` under this live subscription — `startDailyGame` mounts the
  // board BEFORE the replay loop runs. So `s.moves.length > prev.moves.length` is
  // TRUE for each replayed move. We must NOT animate those: the store's `replaying`
  // flag (set around resumeInProgress's loop) suppresses both the spawn-pop AND the
  // line-clear overlays below, so the recovered state paints STATICALLY. Without it
  // a refreshed partial daily game would re-enact every pop/celebration on recovery.
  const moveHappened = !s.replaying && s.moves.length > prev.moves.length;
  // The MOVED ball is relocated, not «appearing» — it must not pop either (§2.4).
  // Its destination is the last move's (r,c). Only the next-balls spawned around
  // it pop. (On a direct-clear the dst is cleared back to empty/ghost, so there's
  // no ball there to pop regardless — the overlay handles the celebration.)
  const movedTo = moveHappened ? s.moves[s.moves.length - 1] : undefined;

  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      const i = r * N + c;
      const cell = el.cells[i]!;
      const isSelected = s.selectedCell?.r === r && s.selectedCell?.c === c;
      cell.classList.toggle('sel', !!isSelected);

      // .unreachable — только для ПУСТЫХ клеток когда есть выделение и пути нет.
      const isEmpty = grid[r]![c] === 0;
      const unreachable = mask !== null && isEmpty && !mask[r]![c];
      cell.classList.toggle('unreachable', unreachable);

      const want = computeCellWant(grid[r]![c], ghostIndex[i] ?? 0);
      if (want !== el.rendered[i]) {
        const isMovedBall = movedTo !== undefined && movedTo.r === r && movedTo.c === c;
        const animateAppear = moveHappened && !isMovedBall;
        renderCellContents(cell, want, animateAppear);
        el.rendered[i] = want;
      }
    }
  }

  // Line-clear animation (board-feedback-animations.md §2.1 + §2.3 v0.2): читаем
  // ЧЕСТНЫЙ контракт событий хода (`s.lastMoveEvents`), а не угадываем по diff
  // `renderedBefore`/`destEmpty`. Движок отдаёт ровно то, что очистил, и где шар
  // вырос (`grown`) — детект тривиален и полон (исправляет два класса багов: сбор
  // на гост-клетку §E-3/AC-9 и спавн, замкнувший линию вне хода-источника AC-10).
  //
  // Триггер: случился НОВЫЙ принятый ход (moves вырос) И движок отдал события.
  // Используем тот же `moveHappened` gate, что и spawn-pop выше: он уже включает
  // `!s.replaying`, поэтому исключает first-paint И daily-recovery replay (§2.4 /
  // AC-13) — при восстановлении moves РАСТУТ под этой подпиской, но флаг `replaying`
  // подавляет overlay'и → собранные при восстановлении линии не «празднуются» заново.
  //
  // Сценарии (§2.1):
  //   viaSpawn===false → direct-move clear: 600мс simple overlay на КАЖДОЙ клетке
  //     (включая dst-поставленный-шар; overlay полноразмерный absolute перекрывает
  //     ре-рендеренный гост §E-3).
  //   viaSpawn===true  → spawn-triggered: 800мс; на каждой клетке grow-overlay если
  //     `grown` (next-бал вырос в линию), иначе hold-overlay.
  // Цвет overlay = `cell.color` из контракта (= цвет линии). Source в `cleared`
  // не входит (движок исключил его — I-2). Overlay рисуется ПОСЛЕ diff-loop, поверх
  // содержимого клетки.
  const events = moveHappened ? s.lastMoveEvents : null;
  if (events && events.cleared.length > 0) {
    for (const cb of events.cleared) {
      const cell = el.cells[cb.r * N + cb.c];
      if (!cell) continue;
      if (events.viaSpawn) {
        if (cb.grown) {
          spawnLineClearGrowOverlay(cell, cb.color);
        } else {
          spawnLineClearHoldOverlay(cell, cb.color);
        }
      } else {
        spawnLineClearOverlay(cell, cb.color);
      }
    }
  }

  // Niche: 3 fixed slots, visible in predict + classic, HIDDEN in blind
  // (extra-modes §2.1). `renderNiche` toggles `visibility` so the layout (and the
  // board geometry) stays identical across modes — only the balls hide.
  renderNiche(el.nicheEl, next, showNiche);

  // Stats + score.
  el.scoreEl.textContent = formatNumber(getScore(s));
  el.movesEl.textContent = String(s.moves.length);

  // Buttons. In PRE-START PREVIEW the PRIMARY rail button becomes «Начать» (the
  // only trigger that starts the scored game — AC-2.2; wireActions branches on
  // preStart). After start the primary reverts to «Завершить заход» (locked once
  // finished). The SECONDARY button is «Перебросить старт» in preview (RP-2 / AC-A.3 —
  // re-roll a fresh server session), reverting to «Новая игра» / «К доске дня» once
  // scoring begins (label set by updateContextLabels below for the non-preview case).
  const ended = !!s.finishReason;
  const preStart = s.preStart;
  el.endBtn.disabled = preStart ? false : ended; // «Начать» enabled in preview
  el.endBtn.textContent = preStart ? t('game.board.btnStart') : t('game.board.btnFinish');
  // Attract-беат кнопки «Начать» (триггерный, owner-final 2026-06-28) — НЕ здесь:
  // мягкий намёк при входе в pre-start + сильный зов на промах-тап по доске навешивает
  // lifecycle `applyPreStartNudge` в mountBoardScreen (lib/attract-beat). Постоянной
  // пульсации в покое больше нет (прежний `.cta-attract` infinite breathe отвергнут).
  // «Перебросить старт» visible+enabled ONLY in preview (must-visible §0.B, AC-UI.1);
  // after «Начать» it reverts to «Новая игра» (AC-UI.2 — no display:none ghost; the
  // same button element is relabelled, never hidden).
  el.newBtn.disabled = false;
  if (preStart) el.newBtn.textContent = t('game.board.btnReroll');

  // Apply context-specific labels (daily vs casual / preview). Cheap — text only.
  const screenRoot = el.boardEl.closest<HTMLElement>('.board-screen')!;
  screenRoot.classList.toggle('board-screen--prestart', preStart);
  updateContextLabels(screenRoot, s);
}

function computeCellWant(cellValue: CellValue, ghostColor: number): string {
  if (cellValue !== 0) return `b${cellValue}`;
  if (ghostColor !== 0) return `g${ghostColor}`;
  return '';
}

function buildGhostIndex(next: NextBall[]): number[] {
  const idx = new Array<number>(N * N).fill(0);
  for (const g of next) {
    idx[g.r * N + g.c] = g.color;
  }
  return idx;
}

/**
 * All-zero ghost index — fed to the cell renderer in non-predict modes (classic /
 * blind) so NO ghosts paint on cells (extra-modes §2.1). Frozen, reused (the engine
 * still generates `next`; we just don't show it as ghosts).
 */
const EMPTY_GHOST_INDEX: readonly number[] = new Array<number>(N * N).fill(0);

function renderCellContents(cell: HTMLElement, want: string, animateAppear: boolean): void {
  if (!want) {
    cell.innerHTML = '';
    return;
  }
  if (want.startsWith('b')) {
    const color = want.slice(1);
    if (animateAppear) {
      // Real-ball appearing DURING PLAY (a move just spawned/grew it): one-shot
      // appearance via shared module helper (база `.ball` без animation — §2.4).
      // Снятие класса по завершении внутри applySpawnPop → нет моргания (AC-11).
      cell.innerHTML = `<div class="ball bc${color}"></div>`;
      const ball = cell.firstElementChild as HTMLElement | null;
      if (ball) applySpawnPop(ball);
    } else {
      // STARTING layout (first paint / fresh start / resumed-daily final state):
      // render STATICALLY (`.ball.static`, no animation), consistent with the daily
      // preview (`board-view.renderStaticGrid`). No spawn-pop → no «refresh» flash
      // when the live board mounts on «Играть доску дня» (§2.4, owner 2026-05-31).
      cell.innerHTML = `<div class="ball static bc${color}"></div>`;
    }
  } else {
    const color = want.slice(1);
    cell.innerHTML = `<div class="ball ghost static bc${color}"></div>`;
  }
}

// Line-clear celebration overlays теперь живут в ОБЩЕМ модуле lib/board-animations
// (owner-directive 2026-05-30 «код через модули» — те же эффекты переиспользует
// экран реплея). Поведение идентично прежним private-функциям (board-feedback-
// animations.md §2.1, Вариант 2 + grow-фаза). См. spawnLineClearOverlay /
// spawnLineClearGrowOverlay / spawnLineClearHoldOverlay (import выше).

function renderNiche(niche: HTMLElement, next: NextBall[], show = true): void {
  // `visibility:hidden` (not removal) keeps the niche slot's geometry so the board
  // header is byte-identical across modes — blind just hides the balls (§2.1).
  niche.style.visibility = show ? 'visible' : 'hidden';
  let html = '';
  for (let i = 0; i < NEXT_BALLS_PER_TURN; i++) {
    const g = next[i];
    html += `<div class="fn-slot">${g ? `<div class="ball static bc${g.color}"></div>` : ''}</div>`;
  }
  niche.innerHTML = html;
}

// =============================================================================
// End-game detection (T-5: board_full when no empty cells remain;
// dead-end-by-no-valid-moves intentionally deferred per core-daily-board.md §7.7).
// =============================================================================

function maybeDetectEndGame(s: GameStoreState): void {
  if (s.finishReason) return; // already ended
  if (!s.engineState) return;
  // Per spec core-daily-board.md §3.5: `board_full` fires when emptyCells === 0.
  if (getEmptyCellCount(s) === 0) {
    s.endGame('board_full');
    void finalizeGame('board_full');
  }
}

/**
 * Finalize the game: persist replay snapshot для local viewing, и для daily —
 * submit к server + cache played-today + clear in-progress.
 *
 * Per dispatch_t-7 §2 C5/C6 + AC-4: для daily context, the published score is
 * `score_verified` от server, NOT `score_claimed`. Local replay-store still
 * uses `score` от engine для replay screen (replayScore выдаёт identical
 * number — anti-cheat invariant; if there's mismatch, server-side audit fires).
 *
 * Flow:
 *   1. Save local replay-store (mirrors T-5 behavior — used by replay screen).
 *   2. If daily: POST /finish-game; store result in dailySubmitResult; save
 *      played-today cache; clear in-progress cache.
 *   3. Transition к result screen после server response (or timeout).
 *
 * `reason` accepts all three `FinishReason` values incl. `'rollover'` (T-12): a
 * rollover auto-finish submits the current score with `finish_reason='rollover'`
 * and the attempt's own `date_of_day` — the server publishes it into THAT day's
 * leaderboard (anti-cheat.md AC-14 / core-daily-board.md §3.5 п.3). The payload
 * path is identical; only `finish_reason` differs.
 */
async function finalizeGame(reason: FinishReason): Promise<void> {
  const s = useGameStore.getState();
  saveLastGame({
    v: 1,
    seed: s.seed,
    moves: s.moves.slice(),
    mode: s.mode,
    score: getScore(s),
    lines: getLines(s),
    finishReason: reason,
    engineVersion: s.engineVersion,
    finishedAtMs: Date.now(),
  });

  if (s.context.kind === 'all_time') {
    // Non-daily server-scored game (extra-modes, ADR-0013). Submit to the server
    // with the session_id; the server cross-checks the seed against the session
    // and (if a personal best) updates the all-time leaderboard. No rollover for
    // non-daily (§2.2) — `reason` is board_full / player_finish. Result-screen
    // reads `dailySubmitResult` for the verified score / replay url (same field,
    // reused — the non-daily result screen is the same surface, §5.3).
    const allTimeContext = s.context;
    // The run is FINISHED → clear the in-progress cache so «Играть» no longer resumes
    // it (RP-3/RP-4: resume restores only UNFINISHED runs). A transient submit failure
    // is retried from the result-screen (which still holds the finished store state),
    // NOT by resuming into play. Fire-and-forget — the cache is UX-only.
    void clearRun();
    useGameStore.getState().showResult();
    let submitResult: SubmitResult;
    try {
      submitResult = await submitFinishedAllTimeGame({
        context: { kind: 'all_time', sessionId: allTimeContext.sessionId },
        seed: s.seed,
        moves: s.moves.slice(),
        scoreClaimed: getScore(s),
        linesClaimed: getLines(s),
        finishReason: reason,
        mode: s.mode,
      });
    } catch {
      submitResult = { status: 'network', attempt: 0 };
    }
    useGameStore.getState().setDailySubmitResult(submitResult);
    return;
  }

  if (s.context.kind !== 'daily') {
    // Casual: same UX as T-5 — small delay so final paint is visible. The run is
    // finished → clear the in-progress cache (resume restores only unfinished runs).
    void clearRun();
    window.setTimeout(() => useGameStore.getState().showResult(), 250);
    return;
  }

  // Daily branch — submit к server. UI transitions к result screen immediately
  // (shows «submitting» state); result-screen polls store для submit verdict.
  const dailyContext = s.context;
  useGameStore.getState().showResult();

  // ⚠ TEMP dev (owner 2026-05-30): ?dev=daily — без реальной отправки/кэша, сразу
  // тестовый вердикт «Зачтено», чтобы гонять заход→result доски дня многократно.
  if (isDevDaily()) {
    useGameStore.getState().setDailySubmitResult({
      status: 'verified',
      gameId: 'dev',
      scoreVerified: getScore(s),
      linesVerified: getLines(s),
      replayUrl: '#dev',
    });
    return;
  }

  let submitResult: SubmitResult;
  try {
    submitResult = await submitFinishedDailyGame({
      context: { kind: 'daily', dateOfDay: dailyContext.dateOfDay },
      seed: s.seed,
      moves: s.moves.slice(),
      scoreClaimed: getScore(s),
      linesClaimed: getLines(s),
      finishReason: reason,
      mode: 'predict',
    });
  } catch {
    submitResult = { status: 'network', attempt: 0 };
  }

  // Store the result for result-screen consumption.
  useGameStore.getState().setDailySubmitResult(submitResult);

  // P2 (brief-44 §B): record THIS submit's own daily row (server-truth `me`, P1) so the
  // leaderboard paints the freshly-played result the moment the player opens it — without
  // waiting for a read-fetch (SWR ловит mount/focus, НЕ событие «мой сабмит»). Only on a
  // verified submit that carried `me`; SWR reconcile then converges to the live board. No-op
  // for a guest (snapshot keyed by playerId). UX-projection — server-truth is the next fetch.
  if (submitResult.status === 'verified' && submitResult.me) {
    savePendingOwnRow(dailyContext.dateOfDay, submitResult.me);
  }

  // Final-board snapshot (core-daily-board.md §2.3-DONE): capture the final grid +
  // niche NOW, while the store still holds the finished engine state, so the daily
  // done-state renders the board instantly from cache on the same device (no
  // network). Free — the state is already in memory. On a cache miss (other device)
  // `lib/daily-final-state` re-simulates instead. `getGrid` is non-null post-finish.
  const finalGrid = getGrid(s);
  const snapshot = finalGrid
    ? snapshotFinalBoard(finalGrid, getNext(s), s.moves.length, reason)
    : undefined;

  // Cache + cleanup based on outcome — shared discipline module (lib/daily-finish):
  // consumed (verified/duplicate/rejected) → savePlayed + clear; not-consumed
  // (rate_limited/engine_mismatch/network) → keep in-progress for retry
  // (anti-cheat.md §3.5 + core-daily-board.md AC-13 + §7.1). Same module used by
  // result-screen retry and the trigger-B boot path, so the rule can't drift. The
  // snapshot is persisted on the verified path (savePlayed) — see daily-finish.
  await applyDailyCacheDiscipline(dailyContext.dateOfDay, submitResult, snapshot);
}

/**
 * Save in-progress cache after each accepted move. Called from the store subscription
 * in `mountBoardScreen`. Cross-surface persistence (run-persistence-lifecycle.md T-B):
 *   - daily   → `services/daily-cache.saveInProgress` (its own played/rollover machinery).
 *   - all_time→ `lib/in-progress-cache.saveRun` with `sessionId` (resume continues the
 *     same server session — RP-3 / addendum Решение 2).
 *   - casual  → `lib/in-progress-cache.saveRun` (purely local, no session).
 * The non-daily branch was previously gated out (`kind!=='daily' → return`) — in-progress
 * non-daily was LOST (extra-modes §6.1, now closed by T-B).
 */
async function maybeSaveInProgress(s: GameStoreState): Promise<void> {
  if (s.finishReason) return; // ended — don't overwrite/preserve in-progress
  if (s.preStart) return; // pre-start preview is non-scored — nothing to persist
  if (s.moves.length === 0) return; // first valid move triggers cache, per spec §2.2

  if (s.context.kind === 'daily') {
    // Визуальный снимок ТЕКУЩЕЙ доски (grid+next+score+movesCount) из живого состояния
    // движка → кладётся в синхронный in-progress-маркер, чтобы при возврате к незавершённой
    // партии доска дня нарисовалась МГНОВЕННО из снимка, без кадра «загрузка» и переигровки
    // (owner-bug A 2026-06-28). Бесплатно — состояние уже в памяти; снимок — UX-проекция
    // показа (движковый канон seed+moves идёт в IDB). `getGrid` не-null в активной партии.
    const grid = getGrid(s);
    const boardView = grid
      ? snapshotInProgressBoard(grid, getNext(s), getScore(s), s.moves.length)
      : undefined;
    await saveInProgress(
      {
        dateOfDay: s.context.dateOfDay,
        seed: s.seed,
        moves: s.moves.slice(),
        timestamp: Date.now(),
      },
      boardView,
    );
    return;
  }

  // Non-daily (all_time + casual) — generalized in-progress cache (T-B). `startedAt`
  // is preserved across saves (first move's timestamp) so «most recent run» resume
  // tie-break (P1-1) is stable; we approximate it with the existing record's value.
  const startedAt = await runStartedAt();
  if (s.context.kind === 'all_time') {
    await saveRun({
      v: 1,
      kind: 'all_time',
      sessionId: s.context.sessionId,
      seed: s.seed,
      mode: s.mode,
      moves: s.moves.slice(),
      engineVersion: s.engineVersion,
      startedAt,
    });
    // Slide the server session's TTL so a long-running run never expires while played
    // (RP-5 / «Pro играет днями»). Throttled inside (SESSION_HEARTBEAT_THROTTLE_MIN);
    // best-effort, fire-and-forget. Only all_time has a server session (casual doesn't).
    heartbeatOnSave(s.context.sessionId);
    return;
  }
  // casual
  await saveRun({
    v: 1,
    kind: 'casual',
    seed: s.seed,
    mode: s.mode,
    moves: s.moves.slice(),
    engineVersion: s.engineVersion,
    startedAt,
  });
}

/**
 * The `startedAt` to stamp on the current run's save. Keep the FIRST save's timestamp
 * stable across subsequent saves (so the «most recent run» resume tie-break, P1-1, is
 * deterministic): if a run record already exists, reuse its `startedAt`; else now.
 */
async function runStartedAt(): Promise<number> {
  const existing = await loadRun();
  return existing ? existing.startedAt : Date.now();
}

// =============================================================================
// Action wiring
// =============================================================================

function wireActions(el: BoardElements): void {
  el.endBtn.addEventListener('click', () => {
    void (async () => {
      // PRE-START PREVIEW: the primary button is «Начать» → start the scored game
      // (server seed, POST /game/start; AC-2.2). No confirm — nothing to abandon.
      if (useGameStore.getState().preStart) {
        beginScoredGame();
        return;
      }
      if (useGameStore.getState().finishReason) return;
      // «Завершить заход» завершает игру (результат засчитывается) — подтверждаем.
      if (!(await confirmFinish())) return;
      const s = useGameStore.getState();
      if (s.finishReason) return;
      s.endGame('player_finish');
      void finalizeGame('player_finish');
    })();
  });
  el.newBtn.addEventListener('click', () => {
    void (async () => {
      // PRE-START PREVIEW: the secondary button is «Перебросить старт» (RP-2 / AC-A.3)
      // — re-roll a fresh server session. No confirm (nothing scored yet to abandon).
      if (useGameStore.getState().preStart) {
        await rerollPreview();
        return;
      }
      // «Новая игра» (all_time/casual) → confirm + restart; «К доске дня» (daily) →
      // silent nav (T-C). Single source — `requestRestart` (rail + compact popover).
      await requestRestart();
    })();
  });
}

/**
 * Handle the secondary button's restart intent (T-C / T-D / RP-6+RP-7 +
 * restart-full-completion.md). Branches on context so the confirm + scoring match
 * the action:
 *   - daily   → «К доске дня» is plain NAVIGATION (the attempt is cached + resumable)
 *     → NO confirm, NO submit, just route back (T-C; daily «Новая игра» недоступна — I-D).
 *   - all_time/casual → «Новая игра» is a FULL FINISH (restart-full-completion.md I-A):
 *     `confirmNewGame`, then — if a scored game is in progress (≥1 move) — the current
 *     game is finalised through the SAME full path as «Завершить заход»
 *     (`endGame('restart')` + `finalizeGame('restart')`): result-screen со счётом +
 *     наградами. The «new game» itself starts later via «Играть ещё» (playAgainFromResult)
 *     on that result-screen — there is no silent reset anymore (old `new_game` path gone).
 *     A 0-move run (I-F / AC-11) has nothing to count → straight to a fresh start.
 * Shared by the main rail + the compact popover (single source — no drift).
 */
async function requestRestart(): Promise<void> {
  const s = useGameStore.getState();
  if (s.context.kind === 'daily') {
    // «К доске дня» — silent navigation (the daily attempt stays cached + resumable).
    await restartByContext();
    return;
  }
  // all_time / casual «Новая игра» — explicit reset → confirm.
  if (!(await confirmNewGame())) return;
  // Re-read after the async confirm gap; bail if it already ended (double-tap guard,
  // mirrors the «Завершить заход» path).
  const st = useGameStore.getState();
  if (st.finishReason) return;

  if (isRunAtStake(st)) {
    // ≥1 move (I-A / AC-4): finalise through the SAME full finish path as
    // board_full/player_finish — result-screen + reward-surface — only the
    // `finish_reason` differs (`restart`). The fresh game is started from the
    // result-screen via «Играть ещё» (playAgainFromResult), NOT here.
    st.endGame('restart');
    void finalizeGame('restart');
    return;
  }
  // 0 moves (I-F / AC-11): nothing to count → no empty finish/result-screen, straight
  // to a fresh start.
  await restartByContext();
}

/**
 * «Перебросить старт» (T-A / AC-A.3): re-roll a fresh server session for the SAME
 * mode while still in pre-start preview — a new `POST /game/start` → new session_id
 * + new CSPRNG seed → a different starting layout, `preStart` stays `true` (scoring
 * hasn't begun). The old `status='open'` session is orphaned + expires by TTL
 * (addendum Решение 1; AC-A.4). Shared module `lib/mode-preview` owns the call (one
 * source — same as the lobby card / «Играть ещё»). On failure, a toast (we stay in
 * the current preview — the player can retry).
 */
async function rerollPreview(): Promise<void> {
  await openModePreview(useGameStore.getState().mode);
}

/**
 * Restart per the current game context (shared by the main rail + compact-popover
 * «Новая игра» buttons — single source, no drift):
 *   - daily   → back to the daily screen (one attempt per day, §3.6 — no restart).
 *   - all_time → open a FRESH pre-start PREVIEW of the SAME mode (new session via
 *     `POST /game/start`, lazy-on-preview T-A; «Начать» begins it). On start failure
 *     `openModePreview` toasts; we bounce to the mode-selector so the player can retry.
 *   - casual  → a fresh local game (legacy T-5 surface; dev / `?mode=casual`).
 */
async function restartByContext(): Promise<void> {
  const s = useGameStore.getState();
  if (s.context.kind === 'daily') {
    s.showDaily();
    return;
  }
  if (s.context.kind === 'all_time') {
    const ok = await openModePreview(s.mode);
    // Couldn't get a new session — `openModePreview` already toasted; bounce to the
    // selector so the player has a clean place to retry from.
    if (!ok) useGameStore.getState().showModeSelect();
    return;
  }
  s.startNewGame();
}

// =============================================================================
// Helpers
// =============================================================================

function must<T>(value: T | null): T {
  if (value === null || value === undefined) {
    throw new Error('board-screen: required element missing in template');
  }
  return value;
}

function formatNumber(n: number): string {
  return n.toLocaleString('ru-RU');
}

